import { useEffect, useRef, useState } from "react";
import { IRegisterResponse } from "../models/IRegisterResponse";
import { resendPhoneOtp, verifyPhoneOtp } from "../services/auth";
import { clearUserStorage, getDeviceId, getUserToken, saveUserToken } from "../utils/tokenUtils";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

const OTP_COOLDOWN = 180; // saniye (3 dakika)

interface Props {
  registerData: IRegisterResponse | null;
  onSuccess: () => void;
  onPhoneVerified?: (verificationId: string) => void; // register akışında email doğrulama adımı için
  modalId?: string;
  phoneNumber?: string;
  verificationId?: string;
  sentAt?: number; // timestamp — OTP gönderildiğinde parent set eder, sayaç buradan başlar
}

function OtpVerificationPopup({
  registerData,
  onSuccess,
  onPhoneVerified,
  modalId = "otpVerificationModal",
  phoneNumber,
  verificationId,
  sentAt,
}: Props) {
  const { t } = useLanguage();
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const closeRef = useRef<HTMLButtonElement>(null);

  // verificationId — parent'tan gelir, resend'de güncellenir
  const [activeVerifId, setActiveVerifId] = useState(
    verificationId ?? registerData?.verificaitonId ?? ""
  );

  useEffect(() => {
    if (verificationId) setActiveVerifId(verificationId);
  }, [verificationId]);

  useEffect(() => {
    if (registerData?.verificaitonId) setActiveVerifId(registerData.verificaitonId);
  }, [registerData]);

  // Sayaç — localStorage'a göre (modalId başına farklı key)
  const storageKey = `otpSentAt_${modalId}`;

  const calcSecondsLeft = () => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return 0;
    const elapsed = Math.floor((Date.now() - parseInt(stored)) / 1000);
    return Math.max(0, OTP_COOLDOWN - elapsed);
  };

  // Sayfa açılışında localStorage'dan oku (kapatıp açma senaryosu)
  // Kayıt yoksa 0 — sayaç ancak OTP gönderildiğinde başlar
  const [secondsLeft, setSecondsLeft] = useState(() => calcSecondsLeft());

  // Parent'tan gelen sentAt değişince (Doğrula'ya tıklanınca) sayacı başlat
  useEffect(() => {
    if (!sentAt) return;
    localStorage.setItem(storageKey, sentAt.toString());
    setSecondsLeft(OTP_COOLDOWN);
  }, [sentAt]);

  useEffect(() => {
    const interval = setInterval(() => setSecondsLeft(calcSecondsLeft()), 1000);
    return () => clearInterval(interval);
  }, [storageKey]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const otp = digits.join("");
  const isComplete = otp.length === 6 && digits.every((d) => d !== "");
  const canResend = secondsLeft === 0;

  const maskedPhone = (() => {
    const phone = registerData?.customerTelNo ?? phoneNumber ?? "";
    if (phone.length <= 4) return phone;
    return phone.slice(0, -4).replace(/\d/g, "*") + phone.slice(-4);
  })();

  // OTP doğrulama — otpCode direkt alır (state async olduğu için)
  const doVerify = async (otpCode: string) => {
    if (otpCode.length !== 6 || loading) return;
    setError("");
    setLoading(true);
    try {
      if (!activeVerifId) {
        setError("Doğrulama bilgisi bulunamadı. Lütfen tekrar deneyin.");
        return;
      }
      const verifyRes = await verifyPhoneOtp(activeVerifId, otpCode, getDeviceId());
      // Backend bu çağrıda aynı cihaz için YENİ token üretip öncekini geçersiz kılar.
      // Register akışında bu ilk login token'ı; profilde telefon doğrularken de mevcut
      // token artık ölü olduğundan yenisini kaydetmek gerekir (yoksa her istek 401 → refresh).
      const currentUserId = getUserToken()?.userId;
      if (verifyRes.data.token && (registerData || currentUserId === verifyRes.data.userId)) {
        if (registerData) clearUserStorage();
        saveUserToken({
          token: verifyRes.data.token,
          expiration: verifyRes.data.expiration,
          secretKey: verifyRes.data.secretKey,
          userId: verifyRes.data.userId,
          // refreshToken kaydedilmezse 60 dk sonra token yenilenemez ve kullanıcı dışarı atılır
          refreshToken: verifyRes.data.refreshToken,
          refreshTokenExpiration: verifyRes.data.refreshTokenExpiration,
        });
      }
      closeRef.current?.click();
      // Register akışında email doğrulama adımı varsa önce onu aç
      if (registerData && onPhoneVerified) {
        onPhoneVerified(activeVerifId);
      } else {
        onSuccess();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t("otp.error")));
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    const cleaned = value.replace(/\D/g, "");

    // Bazı tarayıcılar autocomplete ile tüm kodu tek input'a gönderir
    if (cleaned.length > 1) {
      const code = cleaned.slice(0, 6);
      const newDigits = ["", "", "", "", "", ""];
      code.split("").forEach((c, i) => { newDigits[i] = c; });
      setDigits(newDigits);
      const nextEmpty = newDigits.findIndex((d) => !d);
      inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
      if (code.length === 6) doVerify(code);
      return;
    }

    const digit = cleaned.slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (digit && index === 5) {
      const fullOtp = newDigits.join("");
      if (fullOtp.length === 6) doVerify(fullOtp);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newDigits = ["", "", "", "", "", ""];
    pasted.split("").forEach((char, i) => { newDigits[i] = char; });
    setDigits(newDigits);
    const nextEmpty = newDigits.findIndex((d) => !d);
    inputRefs.current[nextEmpty === -1 ? 5 : nextEmpty]?.focus();
    if (pasted.length === 6) doVerify(pasted);
  };

  const handleReset = () => {
    setDigits(["", "", "", "", "", ""]);
    setError("");
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  };

  const handleResend = async () => {
    const phone = phoneNumber ?? registerData?.customerTelNo ?? "";
    if (!phone) return;
    try {
      // activeVerifId: kayıtta verificaitonId, profilde verification.id'den gelen değer
      const res = await resendPhoneOtp(phone, activeVerifId);
      setActiveVerifId(res.data.verificationId);
      localStorage.setItem(storageKey, Date.now().toString());
      setSecondsLeft(OTP_COOLDOWN);
      setDigits(["", "", "", "", "", ""]);
      setError("");
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(getApiErrorMessage(err, t("otp.otpFailed")));
    }
  };

  return (
    <div
      className="modal fade"
      id={modalId}
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "360px" }}>
        <div className="modal-content rounded-4 shadow">
          {/* X butonu */}
          <button
            ref={closeRef}
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3"
            data-bs-dismiss="modal"
            aria-label="Kapat"
            style={{ zIndex: 1 }}
          />

          <div className="modal-body px-4 py-4 text-center">
            <div className="mb-2">
              <i className="bi bi-shield-lock-fill" style={{ fontSize: "3rem", color: "#c20e1a" }}></i>
            </div>

            <h6 className="fw-bold fs-5 mb-1">{t("otp.title")}</h6>
            <p className="text-muted small mb-3">
              {t("otp.subtitle", { phone: maskedPhone })}
            </p>

            {/* Sayaç */}
            <div className="mb-3">
              {secondsLeft > 0 ? (
                <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger px-3 py-2">
                  <i className="bi bi-clock me-1"></i>
                  {t("otp.waitForNew", { time: formatTime(secondsLeft) })}
                </span>
              ) : (
                <span className="badge rounded-pill bg-success bg-opacity-10 text-success px-3 py-2">
                  <i className="bi bi-check-circle me-1"></i>
                  {t("otp.canSendNew")}
                </span>
              )}
            </div>

            {/* OTP Kutucukları */}
            <div className="d-flex justify-content-center gap-2 mb-4" onPaste={handlePaste}>
              {digits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  className={`form-control text-center fw-bold fs-4 p-0${
                    error ? " border-danger" : digit ? " border-success" : ""
                  }`}
                  style={{
                    width: "44px",
                    height: "54px",
                    borderRadius: "10px",
                    backgroundImage: "none",
                    boxShadow: error
                      ? "0 0 0 2px rgba(220,53,69,0.2)"
                      : digit
                      ? "0 0 0 2px rgba(25,135,84,0.2)"
                      : undefined,
                  }}
                  value={digit}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  disabled={loading}
                />
              ))}
            </div>

            {loading && (
              <div className="mb-3">
                <span className="spinner-border spinner-border-sm text-danger me-2" />
                <span className="text-muted small">{t("otp.verifying")}</span>
              </div>
            )}

            {error && (
              <div className="alert alert-danger py-2 small mb-3">
                <i className="bi bi-exclamation-circle me-1"></i>
                {error}
              </div>
            )}

            <button
              type="button"
              className="btn btn-danger w-100 rounded-pill py-2 fw-semibold mb-3"
              onClick={() => doVerify(otp)}
              disabled={!isComplete || loading}
            >
              <i className="bi bi-check-circle me-2"></i>
              {t("otp.verify")}
            </button>

            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-link btn-sm text-muted text-decoration-none"
                onClick={handleReset}
                disabled={loading}
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i>
                {t("otp.clearCode")}
              </button>

              <button
                type="button"
                className="btn btn-link btn-sm text-decoration-none"
                onClick={handleResend}
                disabled={!canResend || loading}
                style={{ color: canResend ? "#c20e1a" : undefined }}
              >
                <i className="bi bi-send me-1"></i>
                {t("otp.resend")}
              </button>
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm w-100 rounded-pill mt-3"
              data-bs-dismiss="modal"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OtpVerificationPopup;
